import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  apiHooks,
  useApiClient,
} from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import type { ZoriPulseScanInvocationChild } from '@lifeomic/react-client';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { AuditListCell } from '../../AIRules/components/AuditListCell';
import {
  formatDateTime,
  getAuditSubjectMemberId,
  normalizeJson,
} from '../../AIRules/utils';
import { memberDetailMessages } from './messages';

const ZORI_PULSE_SCAN_AGENT_NAME = 'zori-pulse-scan';

const IN_FLIGHT_STATUSES = new Set(['pending', 'scheduled', 'processing']);

type ZoriPulseScanTask = {
  id: string;
  agent: string;
  status: string;
  created: number;
  scheduled?: number;
  subjectId: string;
  agentInput: Record<string, unknown>;
};

const CHILD_AGENT_LABELS: Record<string, string> = {
  'zori-pulse-critical-issue-scan': 'Critical issue scan',
  'zori-pulse-confidence-evaluator': 'Confidence evaluator',
  'zori-pulse-document-extraction': 'Document extraction backfill',
  'zori-pulse-conversation-review': 'Conversation review',
  'zori-pulse-coach': 'Zori Coach',
  // Retained so historical tasks recorded before the coach rename still show a
  // friendly label instead of the raw agent slug.
  'zori-pulse-action-plan-update': 'Action plan update',
  'zori-pulse-care-coordination-scan': 'Care coordination scan',
  'zori-pulse-touchpoint': 'Touchpoint scan',
};

const formatStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1);

const formatChildAgent = (agent: string) => CHILD_AGENT_LABELS[agent] ?? agent;

/** Turn a camelCase output key (e.g. `persistedCount`) into a readable label. */
const formatOutputKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, char => char.toUpperCase());

const formatOutputValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return normalizeJson(value as Record<string, unknown>);
  }
  return String(value);
};

const getChildOutputEntries = (
  output: Record<string, unknown> | null | undefined,
): Array<[string, unknown]> =>
  output
    ? Object.entries(output).filter(([, value]) => value !== undefined)
    : [];

const ZoriPulseScanTaskDetail: React.FC<{
  task: ZoriPulseScanTask;
}> = ({ task }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const [children, setChildren] = useState<ZoriPulseScanInvocationChild[]>([]);
  const [hasLoadedChildren, setHasLoadedChildren] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadChildren = async () => {
      setChildren([]);
      setHasLoadedChildren(false);
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/zori-pulse-scan/invocations/:task_id',
          { task_id: task.id },
        );
        if (isCancelled) {
          return;
        }
        setChildren(response.data.child_tasks ?? []);
      } catch {
        // Best-effort: detail rows still render without scan steps.
      } finally {
        if (!isCancelled) {
          setHasLoadedChildren(true);
        }
      }
    };

    void loadChildren();

    return () => {
      isCancelled = true;
    };
  }, [apiClient, task.id]);

  return (
    <Box direction="column" gap={2} padding={3}>
      <Box direction="column" gap={1}>
        <Box direction="row" gap={1}>
          <Text size="table" weight="bold">
            Status:
          </Text>
          <Text size="table">{formatStatus(task.status)}</Text>
        </Box>
        <Box direction="row" gap={1}>
          <Text size="table" weight="bold">
            Created:
          </Text>
          <Text size="table">{formatDateTime(task.created)}</Text>
        </Box>
        <Box direction="row" gap={1}>
          <Text size="table" weight="bold">
            Task ID:
          </Text>
          <Text size="table">{task.id}</Text>
        </Box>
        <Box direction="row" gap={1}>
          <Text size="table" weight="bold">
            Subject:
          </Text>
          <Text size="table">{task.subjectId}</Text>
        </Box>
      </Box>

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(memberDetailMessages.memberPulseScanTasksSteps)}
        </Text>
        {children.length ? (
          children.map(child => {
            const outputEntries = getChildOutputEntries(child.output);
            return (
              <Box
                key={child.task_id}
                direction="column"
                gap={0.5}
                style={{
                  borderLeft: '3px solid var(--chroma-palette-divider)',
                  paddingLeft: 12,
                }}
              >
                <Box direction="row" justify="space-between" gap={1}>
                  <Text size="table" weight="bold">
                    {formatChildAgent(child.agent)}
                  </Text>
                  <Text
                    size="table"
                    color={child.status === 'failed' ? 'negative' : 'default'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {formatStatus(child.status)}
                  </Text>
                </Box>
                <Text size="caption" color="default">
                  {child.task_id} · {formatDateTime(child.created)}
                </Text>
                {outputEntries.length ? (
                  <Box direction="column" gap={0.25} marginTop={0.5}>
                    {outputEntries.map(([key, value]) => (
                      <Box key={key} direction="row" gap={1}>
                        <Text size="caption" weight="bold">
                          {formatOutputKey(key)}:
                        </Text>
                        <Text size="caption">{formatOutputValue(value)}</Text>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Text size="caption" color="default">
                    {formatMessage(
                      memberDetailMessages.memberPulseScanTasksChildNoResult,
                    )}
                  </Text>
                )}
              </Box>
            );
          })
        ) : hasLoadedChildren ? (
          <Text size="table">
            {formatMessage(memberDetailMessages.memberPulseScanTasksNoSteps)}
          </Text>
        ) : (
          <DotLoader size={0} />
        )}
      </Box>

      <Box direction="row" gap={1}>
        <Text size="table" weight="bold">
          Force:
        </Text>
        <Text size="table">{task.agentInput?.force ? 'Yes' : 'No'}</Text>
      </Box>
    </Box>
  );
};

type ZoriPulseScanTasksProps = {
  subjectId: string;
};

export const ZoriPulseScanTasks: React.FC<ZoriPulseScanTasksProps> = ({
  subjectId,
}) => {
  const { formatMessage } = useIntl();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const tasksQuery = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/zori-audit/tasks',
    {
      agent: ZORI_PULSE_SCAN_AGENT_NAME,
      subject_id: subjectId,
      limit: 200,
    },
    {
      // `subject_id` scopes the query to this member server-side (subject
      // index) instead of scanning account-wide tasks. Cache briefly so
      // flipping between the Extraction / Pulse tabs (or re-opening the
      // member) reuses results; in-flight tasks still refresh via the explicit
      // polling `refetch()` below, which ignores staleTime.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      enabled: Boolean(subjectId),
    },
  );

  const memberTasks = useMemo<ZoriPulseScanTask[]>(
    () =>
      (tasksQuery.data ?? [])
        .filter(task => getAuditSubjectMemberId(task.subjectId) === subjectId)
        .slice()
        .sort((a, b) => b.created - a.created),
    [subjectId, tasksQuery.data],
  );

  useEffect(() => {
    if (!memberTasks.some(task => IN_FLIGHT_STATUSES.has(task.status))) {
      return;
    }
    const interval = window.setInterval(() => {
      void tasksQuery.refetch();
    }, 3000);
    return () => window.clearInterval(interval);
  }, [memberTasks, tasksQuery]);

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Pulse Scan' },
        cell: {
          content: (task: ZoriPulseScanTask) => (
            <AuditListCell
              primary={formatStatus(task.status)}
              secondary={task.id}
              timestamp={formatDateTime(task.created)}
            />
          ),
        },
      },
    ],
    [],
  );

  return (
    <Box direction="column" gap={1} style={{ height: '100%', minHeight: 0 }}>
      <AuditMasterDetail
        rows={memberTasks}
        loading={tasksQuery.isLoading}
        loadingVariant="dots"
        emptyMessage={formatMessage(
          memberDetailMessages.memberPulseScanTasksEmpty,
        )}
        noSelectionMessage={formatMessage(
          memberDetailMessages.memberPulseScanTasksSelect,
        )}
        rowId={(task: ZoriPulseScanTask) => task.id}
        listColumns={listColumns}
        detailTitle={(task: ZoriPulseScanTask) => task.id}
        renderDetail={(task: ZoriPulseScanTask) => (
          <ZoriPulseScanTaskDetail task={task} />
        )}
        selectedRowId={selectedTaskId}
        setSelectedRowId={setSelectedTaskId}
      />
    </Box>
  );
};
